import React, { memo, Suspense, useEffect, useState } from "react";
import RouteWithInnerRoutes from "../../../app/components/RouteWithSubRoutes/RouteWithSubRoutes";
import Loading from "../../../app/components/Loading";
import Header from "../Header";
import map from "lodash/map";
import { Switch, Route, Redirect } from "react-router-dom";
import { withRouter } from "react-router-dom";
import styles from "./entryPoint.module.scss";
import { useDispatch, useSelector } from "react-redux";
import {
  getFinalStatusesRequest,
  getStatusesRequest,
} from "../../../selectors/actions/status";
import { defaultPath as dashboardPath } from "../../../dashboard/routes"; 
import { io } from "socket.io-client";
import { webSocketEventsUrl } from "../../../app/conf";
import _ from "lodash";
import OmuniPackage from "../../../../package.json";
import { useTranslation } from "react-i18next";
import { compareVersions } from "../../../shared/helpers/commons";
import { fullLoggedUserSelector } from "../../selectors/loggedUser";
import { isProviderTokenSelector } from "../../../auth/selectors/token";
import { NewSidebar } from "../NewSideBar/NewSidebar";
import { notification } from "../../../shared/helpers/notification";
import { COMMENT_DIRECTION_TYPES } from "../../../tickets/helpers/constants";
import { usePreferences } from "../../../hooks/usePreferences";
import SVG from "react-inlinesvg";
import SnackBarCustom from "../../../shared/components/SnackBarContainer/SnackBarCustom";
import { tasksUpdatedSVG } from "../../../shared/helpers/icons";
import { setTaskUpdatedEvent } from "../../../shared/actions/socketEvents";
 
const VERSION = OmuniPackage.version;

const SOCKET_TRANSPORTS = ["websocket"];

const SOCKET_CONNECT = "connect";

const SOCKET_RECONNECTION_DELAY_MAX = 1200;

const SOCKET_NOTIFICATION = "notification";

const SOCKET_PROFILE_UPDATED = "profile.updated";

const SOCKET_AGENT_STATUS = "agentStatus";

const SOCKET_GET_SOCIAL_INCOMING = "get.social.incoming";

const SOCKET_GET_SOCIAL_OUTGOING = "get.social.outgoing";

const SOCKET_UPDATE_COMMENT = "update.comment";

const SOCKET_NEW_COMMENT = "new.comment";

const SOCKET_NEW_CONNECTION = "newConnection";

export const SOCKET_TASK_CREATED = "task.created";
export const SOCKET_TASK_UPDATED = "task.updated";
export const SOCKET_TASKS_CLOSED = "tasks.closed";

const EntryPoint = withRouter(
  ({
    loggedUser,
    innerRoutes,
    loggedUser: { permisionEntities, _id, changePassword, loginType },
    fetchingPassword,
    setUserLogin,
    signOut,
    location,
    company,
    changeExpiredPasswordRequest,
    setNotificationSocketEvent,
    setAgentStatusEvent,
    setProfileUpdatedEvent,
    permissions: { permissions },
    notifications: { notifications = [] },
    setCommentsSocketEvent,
    setTasksEvent,
    setTaskEvent,
    setSocialOutgoingEvent,
    setSocialIncomingEvent,
    setUpdateCommentEvent,
    setNewCommentEvent,
    addTasksListEvent,
  }) => {
    const dispatch = useDispatch();

    const { expiredPasswordChanged } = useSelector(fullLoggedUserSelector);    

    const { preferences } = usePreferences();

    const [hasUpdateAvailable, setHasUpdateAvailable] = useState(false);

    const { t: translations } = useTranslation();

    const changePasswordRequired =
      changePassword?.changePasswordRequired ?? false;

    const isProviderToken = useSelector(isProviderTokenSelector);

    let historyNotificationsLength = 0;     

    useEffect(() => {
      dispatch(getStatusesRequest());
      dispatch(getFinalStatusesRequest());

      const socket = io(webSocketEventsUrl, {
        transports: SOCKET_TRANSPORTS,
        reconnectionDelayMax: SOCKET_RECONNECTION_DELAY_MAX,
      });

      socket.on(SOCKET_NOTIFICATION, (notificationData) => {
        setNotificationSocketEvent(notificationData);
      });

      socket.on(SOCKET_PROFILE_UPDATED, (profileUpdatedEvent) => {
        setProfileUpdatedEvent(profileUpdatedEvent);
      });

      socket.on(SOCKET_AGENT_STATUS, (agentStatusEvent) => {
        setAgentStatusEvent(agentStatusEvent);
      });

      socket.on(SOCKET_GET_SOCIAL_INCOMING, (getSocialIncomingEvent) => {
        setSocialIncomingEvent(getSocialIncomingEvent);
      });

      socket.on(SOCKET_GET_SOCIAL_OUTGOING, (getSocialOutgoingEvent) => {
        setSocialOutgoingEvent(getSocialOutgoingEvent);
      });

      socket.on(SOCKET_UPDATE_COMMENT, (updateCommentEvent) => {
        setUpdateCommentEvent(updateCommentEvent);
      });     

      socket.on(SOCKET_TASK_CREATED, (newTaskEvent) => {
        addTasksListEvent(newTaskEvent);
      });

      socket.on(SOCKET_TASK_UPDATED, (newTaskEvent) => {
        addTasksListEvent(newTaskEvent);
        dispatch(setTaskUpdatedEvent(newTaskEvent));
      });

      socket.on(SOCKET_CONNECT, function () {
        /**
         * @event SOCKET_NEW_CONNECTION
         *
         * This event is emitted when a new connection is established with the socket server.
         * It sends a request to initialize the connection and retrieve initial data.
         *
         * Data:
         * - versions: An object containing version information.
         *   - omuniVersion: The version of the omuni application.
         *   - omuniLiteVersion: The version of the omuniLite application.
         * - events: An array of events.
         *
         * @param {object} data - The data to be sent with the event.
         *   - userId: The ID of the logged-in user.
         *
         * @param {function} callback - A callback function to handle the server's response.
         *   - error: An error object if an error occurs during the request.
         *   - socketData: The data received from the server.
         *     - events: An array of socket events.
         *     - versions: An object containing version information.
         *       - omuniVersion: The version of the omuni application.
         *
         */

        socket.emit(
          SOCKET_NEW_CONNECTION,
          { userId: loggedUser.crmUserId },
          (error, socketData) => {
            const socketEvents = socketData.events;

            const socketVersion = socketData.versions;

            if (!error && socketEvents) {
              socketEvents.forEach((redisEvent) => {
                if (redisEvent.event === SOCKET_NOTIFICATION) {
                  historyNotificationsLength = +1;
                }
              });

              if (historyNotificationsLength >= notifications.length) {
                socketEvents.forEach((redisEvent) => {
                  if (redisEvent.event === SOCKET_NOTIFICATION) {
                    setNotificationSocketEvent(redisEvent.data);
                  }
                });
              }
            }

            if (
              !error &&
              socketVersion &&
              compareVersions(VERSION, socketVersion?.omuniVersion)
            ) {
              setHasUpdateAvailable(true);
            }
          }
        );
      });

      return () => {
        socket.disconnect();
      };
    }, [preferences]);

    const handleUpdateLatestVersion = () => {
      setHasUpdateAvailable(false);
      window.location.reload(true);
    };

    return (
      <div className={styles.grid}>
        <NewSidebar />
        <div className={styles.main}>           
          <Header
            signOut={signOut}
            innerRoutes={innerRoutes}
            location={location}
          />

          <div className={styles.wrapper}>
            <Suspense fallback={<Loading theme />}>
              <Switch>
                {map(innerRoutes, (route, index) => {
                  if (permisionEntities[route.path]) {
                    return <RouteWithInnerRoutes key={index} {...route} />;
                  } else {
                    return null;
                  }
                })}
                <Route path="*">
                  <Redirect to={dashboardPath} />
                </Route>
              </Switch>
            </Suspense>
          </div>
        </div>

        <SnackBarCustom
          isOpen={hasUpdateAvailable}
          title={translations("home:new_version_available_title")}
          description={translations("home:new_version_available_description")}
          handleAction={handleUpdateLatestVersion}
          iconButton={
            <SVG src={tasksUpdatedSVG} className={styles.iconWrapper} />
          }
        />
      </div>
    );
  }
);

export default memo(EntryPoint);
